import { useState } from 'react';
import { Settings } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { SettingsDialog } from './SettingsDialog';

export interface SectionVisibility {
  executiveSummary: boolean;
  keyFindings: boolean;
  pluginProfile: boolean;
  scoringBreakdown: boolean;
  creatorVerification: boolean;
  aiBom: boolean; // kept for backwards compatibility but not shown in settings
  vulnerabilityAssessment: boolean;
  threatModeling: boolean;
  permissionScope: boolean;
  prioritizedRemediation: boolean;
  riskAcceptanceMatrix: boolean;
  recommendation: boolean;
  complianceCrosswalk: boolean; // kept for backwards compatibility but not shown in settings
  rfiaSection: boolean;
  vibeguardSection: boolean;
  heatmapAivss: boolean;
  heatmapCvss: boolean;
  heatmapAars: boolean;
  heatmapOverall: boolean;
  heatmapVulnerability: boolean;
}

// Keys that are shown in the settings UI (excludes aiBom and complianceCrosswalk)
type SettingsVisibleKeys = Exclude<keyof SectionVisibility, 'aiBom' | 'complianceCrosswalk' | 'rfiaSection' | 'vibeguardSection'>;

interface ReportSettingsProps {
  visibility: SectionVisibility;
  onVisibilityChange: (visibility: SectionVisibility) => void;
}

const sectionLabels: Record<SettingsVisibleKeys, string> = {
  executiveSummary: 'Executive Summary',
  keyFindings: 'Key Findings',
  pluginProfile: 'Plugin Profile',
  scoringBreakdown: 'Scoring Breakdown',
  creatorVerification: 'VendorGuard (Creator Verification)',
  vulnerabilityAssessment: 'Vulnerability Assessment',
  threatModeling: 'Threat Modeling',
  permissionScope: 'Permission Scope Analysis',
  prioritizedRemediation: 'Prioritized Remediation',
  riskAcceptanceMatrix: 'Risk Acceptance Matrix',
  recommendation: 'Final Recommendation',
  heatmapAivss: 'Heatmap: AIVSS Breakdown',
  heatmapCvss: 'Heatmap: CVSS Breakdown',
  heatmapAars: 'Heatmap: AARS Breakdown',
  heatmapOverall: 'Heatmap: Overall Risk',
  heatmapVulnerability: 'Heatmap: Vulnerability Assessment',
};

const ReportSettings = ({ visibility, onVisibilityChange }: ReportSettingsProps) => {
  const [open, setOpen] = useState(false);

  const visibleKeys = Object.keys(sectionLabels) as Array<SettingsVisibleKeys>;

  const items = visibleKeys.map((key) => ({ key, label: sectionLabels[key] }));
  const values = visibleKeys.reduce(
    (acc, key) => ({ ...acc, [key]: visibility[key] }),
    {} as Record<string, boolean>
  );

  const handleToggle = (key: string) => {
    onVisibilityChange({
      ...visibility,
      [key as keyof SectionVisibility]: !visibility[key as keyof SectionVisibility],
    });
  };

  const handleToggleAll = () => {
    const allVisible = visibleKeys.every((k) => visibility[k]);
    const next = !allVisible;
    const updated = Object.keys(visibility).reduce((acc, key) => {
      acc[key as keyof SectionVisibility] = next;
      return acc;
    }, {} as SectionVisibility);
    onVisibilityChange(updated);
  };

  return (
    <>
      <Button variant="outline" className="gap-2" onClick={() => setOpen(true)}>
        <Settings className="w-4 h-4" />
        Settings
      </Button>
      <SettingsDialog
        title="Report Settings"
        description="Customize which sections appear in the report"
        open={open}
        onOpenChange={setOpen}
        items={items}
        values={values}
        onToggle={handleToggle}
        onToggleAll={handleToggleAll}
      />
    </>
  );
};

export default ReportSettings;
